import { useEffect, useState, useRef, useMemo } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { motion, AnimatePresence } from "framer-motion";
import { api, INTENT_COLORS, STATUS_COLORS, fmtUsd, fmtTimeAgo } from "@/lib/nexus";
import { useAuth } from "@/context/AuthContext";
import AgenticActionDialog from "@/components/AgenticActionDialog";
import AgentInspector from "@/components/AgentInspector";
import { AlertTriangle, ArrowRight, Search, ShieldAlert, TestTube2, Radar, Bug, Loader2, X, Users } from "lucide-react";

const ACTIONS = [
  { key: "investigate", label: "Investigate", icon: Search, color: "#00F0FF", desc: "Full autonomous attribution + on-chain correlation." },
  { key: "research", label: "Research", icon: Radar, color: "#9D4EDD", desc: "Historical precedents & similar A2A patterns." },
  { key: "test-transaction", label: "Test Transaction", icon: TestTube2, color: "#FFB000", desc: "Dry-run probe simulating an A2A call against target." },
  { key: "pen-test", label: "Pen Test", icon: Bug, color: "#FF3366", desc: "Enumerate vulnerability surface — endpoints, prompts, guardrails." },
];

function AlertRow({ a, active, onClick }) {
  const border = a.color === "rose" ? "border-nexus-rose/60" : "border-nexus-amber/50";
  const text = a.color === "rose" ? "text-nexus-rose" : "text-nexus-amber";
  return (
    <button
      onClick={onClick}
      data-testid={`alert-row-${a.id}`}
      className={`w-full text-left px-3 py-2.5 border-l-2 ${border} border-b border-nexus-border/60 hover:bg-nexus-panel-2/60 transition ${active ? "bg-nexus-panel-2/70" : ""}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={13} className={`${text} mt-0.5 shrink-0 ${a.severity === "critical" ? "animate-pulse" : ""}`} />
        <div className="min-w-0 flex-1">
          <div className="flex justify-between items-center gap-2">
            <div className={`font-rajdhani uppercase tracking-widest text-[11px] font-semibold ${text} truncate`}>{a.title}</div>
            <span className="text-[10px] font-mono text-nexus-text-3 shrink-0">{fmtTimeAgo(a.timestamp)}</span>
          </div>
          <div className="mt-1 flex justify-between text-[10px] font-mono">
            <span className="text-nexus-cyan truncate">{a.agent_name}</span>
            <span className={text}>{fmtUsd(a.impact_usd)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

// Framework short codes for compact badge display
const FW_CODE = {
  OpenAI: "OA", Anthropic: "AN", LangChain: "LC", CrewAI: "CR",
  AutoGen: "AG", ElizaOS: "EL", LlamaIndex: "LI", Gemini: "GM",
};
// Compact chain codes
const CHAIN_CODE = {
  Ethereum: "ETH", Base: "BASE", Solana: "SOL", Polygon: "POLY",
  Arbitrum: "ARB", Optimism: "OP",
};

function useSize(ref) {
  const [dims, setDims] = useState({ w: 800, h: 460 });
  useEffect(() => {
    const ro = new ResizeObserver((es) => { if (es[0]) setDims({ w: es[0].contentRect.width, h: es[0].contentRect.height }); });
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return dims;
}

function MiniGraph({ nodes, links, targetId, alertTitle, onNodeInspect }) {
  const containerRef = useRef();
  const fgRef = useRef();
  const [hover, setHover] = useState(null);
  const dims = useSize(containerRef);

  // Pin target to center + arrange counterparties radially by framework cluster
  const graphData = useMemo(() => {
    const target = nodes.find(n => n.id === targetId);
    if (!target) return { nodes: [], links: [] };
    const others = nodes.filter(n => n.id !== targetId);

    // Sort so within a framework the more-critical agents appear closer to target
    const byFramework = new Map();
    others.forEach(n => {
      const k = n.framework || "?";
      if (!byFramework.has(k)) byFramework.set(k, []);
      byFramework.get(k).push(n);
    });
    for (const arr of byFramework.values()) {
      arr.sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0));
    }

    const frameworks = Array.from(byFramework.keys()).sort();
    const positioned = [{ ...target, fx: 0, fy: 0 }];
    const n = frameworks.length;
    // Radial slots — each agent gets a UNIQUE angle
    const totalAgents = others.length;
    let idx = 0;
    frameworks.forEach((fw, fi) => {
      const bucket = byFramework.get(fw);
      // Wedge for this framework
      const wedgeStart = (fi / n) * 2 * Math.PI - Math.PI / 2;
      const wedgeSize = (2 * Math.PI) / n;
      bucket.forEach((agent, bi) => {
        // Distribute inside the wedge; leave margin at edges
        const t = (bucket.length === 1) ? 0.5 : (bi + 0.5) / bucket.length;
        const angle = wedgeStart + wedgeSize * t;
        // Alternate ring radius so labels don't overlap for large clusters
        const R = 260 + (bi % 2) * 60;
        positioned.push({
          ...agent,
          fx: Math.cos(angle) * R,
          fy: Math.sin(angle) * R,
        });
        idx++;
      });
    });
    void idx; void totalAgents;
    return {
      nodes: positioned,
      links: links.map(l => ({ ...l })),
    };
  }, [nodes, links, targetId]);

  useEffect(() => {
    if (!fgRef.current) return;
    fgRef.current.d3Force("charge").strength(-30);
    const linkForce = fgRef.current.d3Force("link");
    if (linkForce) linkForce.strength(0);
    setTimeout(() => fgRef.current?.zoomToFit(500, 60), 700);
  }, [graphData, dims]);

  // Compute in/out flow summary for target
  const flowSummary = useMemo(() => {
    let inV = 0, outV = 0, inCount = 0, outCount = 0;
    const intents = new Map();
    links.forEach(l => {
      if (l.target === targetId || l.target?.id === targetId) {
        inV += l.value_usd || 0; inCount += l.count || 0;
      }
      if (l.source === targetId || l.source?.id === targetId) {
        outV += l.value_usd || 0; outCount += l.count || 0;
      }
      const key = l.intent;
      intents.set(key, (intents.get(key) || 0) + (l.value_usd || 0));
    });
    return { inV, outV, inCount, outCount, intents: Array.from(intents.entries()).sort((a, b) => b[1] - a[1]) };
  }, [links, targetId]);

  // Involved intents (for legend)
  const uniqueIntents = useMemo(() => {
    const s = new Set(links.map(l => l.intent));
    return Array.from(s);
  }, [links]);

  const drawNodeCard = (node, ctx) => {
    const isTarget = node.id === targetId;
    const status = STATUS_COLORS[node.status] || "#00F0FF";
    const fwCode = FW_CODE[node.framework] || (node.framework?.slice(0, 2).toUpperCase() || "?");
    const chainCode = CHAIN_CODE[node.chain] || (node.chain?.slice(0, 3).toUpperCase() || "?");

    // Sizing
    const W = isTarget ? 168 : 128;
    const H = isTarget ? 70 : 52;
    const x = node.x - W / 2;
    const y = node.y - H / 2;

    // Backdrop
    ctx.fillStyle = "rgba(10, 17, 34, 0.94)";
    ctx.fillRect(x, y, W, H);
    // Left status bar
    ctx.fillStyle = status;
    ctx.fillRect(x, y, 3, H);
    // Border
    ctx.strokeStyle = isTarget ? "#FF3366" : (node.status !== "normal" ? status : "#334155");
    ctx.lineWidth = isTarget ? 2 : 1;
    if (isTarget) ctx.setLineDash([4, 3]);
    ctx.strokeRect(x + 0.5, y + 0.5, W - 1, H - 1);
    ctx.setLineDash([]);

    // Framework code badge (top-left)
    ctx.fillStyle = "#9D4EDD";
    ctx.fillRect(x + 8, y + 6, 22, 14);
    ctx.fillStyle = "#F8FAFC";
    ctx.font = "700 9px JetBrains Mono";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(fwCode, x + 19, y + 13);

    // Chain code badge (next to framework)
    ctx.fillStyle = "#0891B2";
    ctx.fillRect(x + 34, y + 6, 30, 14);
    ctx.fillStyle = "#F8FAFC";
    ctx.fillText(chainCode, x + 49, y + 13);

    // Risk score chip (top-right)
    const riskBg = node.risk_score >= 70 ? "#FF3366" : node.risk_score >= 40 ? "#FFB000" : "#0891B2";
    ctx.fillStyle = riskBg;
    ctx.fillRect(x + W - 30, y + 6, 24, 14);
    ctx.fillStyle = "#040914";
    ctx.font = "700 10px JetBrains Mono";
    ctx.fillText(String(node.risk_score), x + W - 18, y + 13);

    // Name
    ctx.fillStyle = isTarget ? "#FF3366" : "#F8FAFC";
    ctx.font = isTarget ? "700 13px Rajdhani" : "600 12px Rajdhani";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const name = node.name.length > (isTarget ? 20 : 14) ? node.name.slice(0, isTarget ? 20 : 14) + "…" : node.name;
    ctx.fillText(name.toUpperCase(), x + 8, y + 30);

    // Balance line
    ctx.fillStyle = "#94A3B8";
    ctx.font = "500 10px JetBrains Mono";
    const balStr = node.balance_usd >= 1e6 ? `$${(node.balance_usd / 1e6).toFixed(1)}M`
                 : node.balance_usd >= 1e3 ? `$${(node.balance_usd / 1e3).toFixed(1)}K`
                 : `$${(node.balance_usd || 0).toFixed(0)}`;
    ctx.fillText(`bal ${balStr}`, x + 8, y + 42);
    // Tx count on right
    ctx.textAlign = "right";
    ctx.fillText(`tx ${node.tx_count.toLocaleString()}`, x + W - 8, y + 42);
    ctx.textAlign = "left";

    // Target extra row: owner
    if (isTarget && node.owner) {
      ctx.fillStyle = "#FFB000";
      ctx.font = "500 10px JetBrains Mono";
      const owner = node.owner.length > 24 ? node.owner.slice(0, 24) + "…" : node.owner;
      ctx.fillText(owner, x + 8, y + 56);
      // Alert badge
      ctx.fillStyle = "#FF3366";
      ctx.fillRect(x + W - 46, y + H - 16, 40, 12);
      ctx.fillStyle = "#F8FAFC";
      ctx.font = "700 8px JetBrains Mono";
      ctx.textAlign = "center";
      ctx.fillText("TARGET", x + W - 26, y + H - 9);
    }
  };

  const drawEdgeLabel = (link, ctx) => {
    const sx = link.source.x, sy = link.source.y;
    const tx = link.target.x, ty = link.target.y;
    if (sx == null || tx == null) return;
    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;
    const val = link.value_usd || 0;
    const label = `${link.intent} · $${val >= 1e3 ? (val / 1e3).toFixed(1) + "K" : val.toFixed(0)} · ${link.count}tx`;
    ctx.font = "500 9px JetBrains Mono";
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = "rgba(4,9,20,0.95)";
    ctx.fillRect(mx - tw / 2 - 3, my - 8, tw + 6, 12);
    ctx.strokeStyle = link.intent_color || "#334155";
    ctx.lineWidth = 0.8;
    ctx.strokeRect(mx - tw / 2 - 3, my - 8, tw + 6, 12);
    ctx.fillStyle = link.intent_color || "#94A3B8";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(label, mx, my - 2);
  };

  return (
    <div ref={containerRef} className="relative border border-nexus-border bg-nexus-panel/30" style={{ height: 520 }}>
      <div className="absolute top-2 left-3 z-10 panel-title"><span className="dot" />Context Graph · {nodes.length} agents · {links.length} A2A edges</div>

      {/* Flow summary — top right */}
      <div className="absolute top-2 right-3 z-10 flex items-center gap-2">
        <button data-testid="ctx-zoom-in" onClick={() => fgRef.current?.zoom((fgRef.current?.zoom() || 1) * 1.4, 300)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70" title="Zoom in">
          <span className="font-mono text-[11px]">+</span>
        </button>
        <button data-testid="ctx-zoom-out" onClick={() => fgRef.current?.zoom((fgRef.current?.zoom() || 1) / 1.4, 300)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70" title="Zoom out">
          <span className="font-mono text-[11px]">−</span>
        </button>
        <button data-testid="ctx-zoom-fit" onClick={() => fgRef.current?.zoomToFit(400, 60)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70 text-[10px] font-mono uppercase px-2" title="Fit view">
          fit
        </button>
        <div className="border border-nexus-cyan/50 bg-nexus-bg/70 px-2 py-1 text-[10px] font-mono uppercase tracking-widest">
          <span className="text-nexus-text-3">IN</span> <span className="text-nexus-cyan">{fmtUsd(flowSummary.inV)}</span> <span className="text-nexus-text-3">·</span> {flowSummary.inCount}tx
        </div>
        <div className="border border-nexus-rose/50 bg-nexus-bg/70 px-2 py-1 text-[10px] font-mono uppercase tracking-widest">
          <span className="text-nexus-text-3">OUT</span> <span className="text-nexus-rose">{fmtUsd(flowSummary.outV)}</span> <span className="text-nexus-text-3">·</span> {flowSummary.outCount}tx
        </div>
      </div>

      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        width={dims.w} height={dims.h}
        backgroundColor="rgba(4,9,20,0)"
        cooldownTicks={0}
        d3AlphaDecay={1}
        d3VelocityDecay={1}
        nodeRelSize={8}
        linkColor={(l) => (INTENT_COLORS[l.intent] || "#334155") + "CC"}
        linkWidth={(l) => Math.max(1.2, Math.min(3.5, 0.4 + Math.log10(1 + (l.value_usd || 1)) * 0.6))}
        linkDirectionalArrowLength={7}
        linkDirectionalArrowRelPos={0.9}
        linkDirectionalArrowColor={(l) => INTENT_COLORS[l.intent] || "#94A3B8"}
        linkDirectionalParticles={(l) => ((l.value_usd || 0) > 1000 ? 2 : 1)}
        linkDirectionalParticleWidth={1.6}
        linkDirectionalParticleSpeed={0.006}
        linkCanvasObjectMode={() => "after"}
        linkCanvasObject={drawEdgeLabel}
        onNodeHover={setHover}
        onNodeClick={(n) => onNodeInspect?.(n.id)}
        nodeCanvasObject={drawNodeCard}
        nodePointerAreaPaint={(node, color, ctx) => {
          const W = node.id === targetId ? 168 : 128;
          const H = node.id === targetId ? 70 : 52;
          ctx.fillStyle = color;
          ctx.fillRect(node.x - W / 2, node.y - H / 2, W, H);
        }}
      />

      {/* Legend — bottom left */}
      <div className="absolute bottom-2 left-3 z-10 panel px-3 py-2 text-[10px] font-mono uppercase tracking-widest flex flex-col gap-1 max-w-[240px]">
        <div className="text-nexus-text-3">{alertTitle ? `edge legend · ${alertTitle}` : "edge legend"}</div>
        <div className="flex flex-wrap gap-1.5">
          {uniqueIntents.slice(0, 8).map(it => (
            <span key={it} className="flex items-center gap-1">
              <span className="w-3 h-[2px]" style={{ background: INTENT_COLORS[it] || "#94A3B8" }} />
              <span className="text-nexus-text-2 normal-case tracking-normal">{it}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Hover panel — bottom right */}
      {hover && (
        <div className="absolute bottom-2 right-3 z-10 panel px-3 py-2 text-[11px] font-mono max-w-[280px]">
          <div className="text-nexus-cyan font-rajdhani font-semibold tracking-widest uppercase text-[12px]">{hover.name}</div>
          <div className="text-nexus-text-2 mt-0.5">{hover.framework} · {hover.chain} · owner {hover.owner || "—"}</div>
          <div className="text-nexus-text-2">risk <span style={{ color: STATUS_COLORS[hover.status] }}>{hover.risk_score}</span> · balance {fmtUsd(hover.balance_usd)} · tx {hover.tx_count}</div>
          <div className="hash mt-1">{hover.short_address}</div>
        </div>
      )}
    </div>
  );
}

function AlertDetail({ alertId, onAction, onInspect }) {
  const { canWrite } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!alertId) return;
    setData(null);
    api.get(`/alerts/${alertId}`).then((r) => setData(r.data));
  }, [alertId]);

  if (!alertId) {
    return (
      <div className="flex-1 flex items-center justify-center text-nexus-text-2">
        <div className="text-center">
          <AlertTriangle size={32} className="mx-auto text-nexus-text-3" />
          <div className="mt-3 panel-title">Select an alert to drill in</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex-1 flex items-center justify-center text-nexus-text-2">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  const { alert, target, counterparties, related_transactions, logic_flow, context_graph } = data;
  const alertColor = alert.color === "rose" ? "#FF3366" : "#FFB000";

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      {/* Header */}
      <div className="panel p-4 brackets">
        <span className="b1" /><span className="b2" />
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} style={{ color: alertColor }} className={alert.severity === "critical" ? "animate-pulse" : ""} />
              <span className="text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5" style={{ borderColor: alertColor, color: alertColor }}>
                {alert.severity}
              </span>
              <span className="text-[10px] font-mono text-nexus-text-3">{fmtTimeAgo(alert.timestamp)} ago</span>
            </div>
            <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-2 uppercase" style={{ color: alertColor }}>{alert.title}</h1>
            <p className="text-[13px] text-nexus-text-2 mt-1">{alert.description}</p>
          </div>
          <div className="text-right shrink-0">
            <div className="panel-title">Impact</div>
            <div className="metric-hero text-2xl" style={{ color: alertColor }}>{fmtUsd(alert.impact_usd)}</div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {canWrite ? (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ACTIONS.map((a) => (
          <button
            key={a.key}
            data-testid={`action-${a.key}`}
            onClick={() => onAction(a.key)}
            className="panel p-3 text-left brackets group hover:bg-nexus-panel-2/60 transition"
            style={{}}
          >
            <span className="b1" /><span className="b2" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <a.icon size={14} style={{ color: a.color }} />
                <span className="font-rajdhani font-semibold uppercase tracking-widest text-[12px]" style={{ color: a.color }}>
                  {a.label}
                </span>
              </div>
              <ArrowRight size={13} className="text-nexus-text-3 group-hover:text-nexus-text group-hover:translate-x-0.5 transition" />
            </div>
            <div className="text-[11px] text-nexus-text-2 mt-1">{a.desc}</div>
          </button>
        ))}
      </div>
      ) : (
        <div data-testid="alerts-readonly-notice" className="panel p-3 brackets text-[12px] font-mono text-nexus-text-2 flex items-center gap-2">
          <span className="b1" /><span className="b2" />
          <ShieldAlert size={14} className="text-nexus-amber shrink-0" />
          Read-only access — agentic actions (investigate / research / test-transaction / pen-test) require write permission. Ask an admin to upgrade your role.
        </div>
      )}

      {/* Logic flow */}
      <div className="panel p-3 brackets">
        <span className="b1" /><span className="b2" />
        <div className="panel-title mb-2"><span className="dot" />Alert Logic Flow</div>
        <div className="space-y-2">
          {logic_flow.map((s) => (
            <div key={s.step} className="border-l-2 border-nexus-cyan/40 pl-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan">{s.step}</div>
              <div className="text-[12px] text-nexus-text mt-0.5">{s.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Context graph */}
      {context_graph.nodes.length > 0 && (
        <MiniGraph
          nodes={context_graph.nodes}
          links={context_graph.links}
          targetId={alert.agent_id}
          alertTitle={alert.title}
          onNodeInspect={onInspect}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Target */}
        {target && (
          <div className="panel p-3 brackets">
            <span className="b1" /><span className="b2" />
            <div className="panel-title mb-2"><span className="dot" style={{ background: "#FF3366", boxShadow: "0 0 8px #FF3366" }} />Target Agent</div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[target.status], boxShadow: `0 0 8px ${STATUS_COLORS[target.status]}` }} />
              <div className="font-rajdhani font-semibold uppercase tracking-widest text-[14px]">{target.name}</div>
            </div>
            <div className="hash mt-1">{target.short_address}</div>
            <div className="text-[11px] font-mono text-nexus-text-2 mt-1">
              {target.framework} · {target.chain} · risk <span style={{ color: STATUS_COLORS[target.status] }}>{target.risk_score}</span> · {target.owner}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {target.intent_tags.map((t) => (
                <span key={t} className="text-[9px] font-mono uppercase tracking-widest border border-nexus-violet/50 text-nexus-violet px-1.5 py-0.5">{t}</span>
              ))}
            </div>
          </div>
        )}

        {/* Counterparties */}
        <div className="panel p-3 brackets">
          <span className="b1" /><span className="b2" />
          <div className="panel-title mb-2"><Users size={11} className="inline mr-1" />Counterparties · {counterparties.length}</div>
          <div className="space-y-1 max-h-[220px] overflow-auto">
            {counterparties.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-[11px] font-mono border border-nexus-border/60 px-2 py-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STATUS_COLORS[c.status] }} />
                  <span className="text-nexus-text truncate">{c.name}</span>
                </div>
                <span className="text-nexus-text-2 shrink-0">{c.framework} · {c.chain}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related transactions */}
      <div className="panel overflow-auto max-h-[280px]">
        <div className="px-3 py-2 border-b border-nexus-border panel-title"><span className="dot" />Related A2A Transactions</div>
        <table className="data-table">
          <thead><tr><th>Time</th><th>Hash</th><th>From → To</th><th>Intent</th><th>Value</th></tr></thead>
          <tbody>
            {related_transactions.map((t) => (
              <tr key={t.id}>
                <td className="text-nexus-text-2">{fmtTimeAgo(t.timestamp)}</td>
                <td className="text-nexus-cyan">{t.short_hash}</td>
                <td>{t.from_name} → {t.to_name}</td>
                <td>
                  <span className="text-[9px] uppercase tracking-widest border px-1 py-0.5" style={{ borderColor: INTENT_COLORS[t.intent], color: INTENT_COLORS[t.intent] }}>
                    {t.intent}
                  </span>
                </td>
                <td>{fmtUsd(t.value_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [inspectId, setInspectId] = useState(null);

  useEffect(() => {
    api.get("/alerts").then((r) => {
      setAlerts(r.data.items);
      if (r.data.items.length > 0) setSelectedId(r.data.items[0].id);
    });
  }, []);

  return (
    <div className="flex h-[calc(100vh-48px)]">
      {/* Alert list column */}
      <aside className="w-72 border-r border-nexus-border bg-nexus-panel/40 flex flex-col shrink-0">
        <div className="px-3 py-3 border-b border-nexus-border">
          <div className="panel-title"><span className="dot" style={{ background: "#FF3366", boxShadow: "0 0 8px #FF3366" }} />Anomaly Alerts</div>
          <div className="mt-1 font-rajdhani font-bold text-lg tracking-widest uppercase">
            {alerts.filter((a) => a.severity === "critical").length} <span className="text-nexus-rose">critical</span>
            <span className="text-nexus-text-3"> · </span>
            {alerts.filter((a) => a.severity === "watch").length} <span className="text-nexus-amber">watch</span>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {alerts.map((a) => (
            <AlertRow key={a.id} a={a} active={selectedId === a.id} onClick={() => setSelectedId(a.id)} />
          ))}
        </div>
      </aside>

      <AlertDetail alertId={selectedId} onAction={setActionKey} onInspect={setInspectId} />

      <AgentInspector agentId={inspectId} onClose={() => setInspectId(null)} onTrace={() => {}} />

      <AnimatePresence>
        {actionKey && (
          <AgenticActionDialog
            actionKey={actionKey}
            alertId={selectedId}
            onClose={() => setActionKey(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
